import React from 'react';
import type {
  ComponentSize,
  ComponentVariant,
} from 'src/modules/shared/types/component.type';
import { classes } from 'src/modules/shared/utils/styles.util';

interface CountBadgeProps {
  count: number;
  loading?: boolean;
  size?: ComponentSize;
  variant?: ComponentVariant;
  className?: string;
}

const sizeClasses: Record<ComponentSize, string> = {
  '2xs': 'h-4 min-w-[16px] px-0.5 text-[8px]',
  xs: 'h-4.5 min-w-[18px] px-1 text-[9px]',
  sm: 'h-5 min-w-[20px] px-1 text-[10px]',
  md: 'h-6 min-w-[24px] px-2 text-[11px]',
  lg: 'h-7 min-w-[28px] px-2.5 text-[12px]',
};

const variantClasses: Record<ComponentVariant, string> = {
  primary: 'bg-primary-100 text-primary-700 border border-primary-200',
  secondary: 'bg-secondary-100 text-secondary-600 border border-secondary-200',
  neutral: 'bg-gray-100 text-gray-600 border border-gray-200',
  danger: 'bg-red-50 text-red-600 border border-red-100',
  warning: 'bg-amber-50 text-amber-600 border border-amber-100',
  success: 'bg-emerald-50 text-emerald-600 border border-emerald-100',
  info: 'bg-blue-50 text-blue-600 border border-blue-100',
  whatsapp: 'bg-green-50 text-green-600 border border-green-100',
  call: 'bg-purple-50 text-purple-600 border border-purple-100',
  email: 'bg-orange-50 text-orange-600 border border-orange-100',
};

/**
 * A circular/pill badge specifically for displaying counts.
 * Ensures consistent height and centering across the application.
 * Uses standard ComponentVariant and ComponentSize types.
 */
export const CountBadge: React.FC<CountBadgeProps> = ({
  count,
  loading = false,
  size = 'md',
  variant = 'secondary',
  className = '',
}) => {
  if (loading) {
    return (
      <div
        className={classes(
          'animate-pulse rounded-full border',
          sizeClasses[size],
          variantClasses[variant],
          'min-w-0 px-0',
          size === '2xs' && 'w-4',
          size === 'xs' && 'w-[18px]',
          size === 'sm' && 'w-5',
          size === 'md' && 'w-6',
          size === 'lg' && 'w-7',
          className
        )}
      />
    );
  }

  return (
    <div
      className={classes(
        'flex items-center justify-center rounded-full font-bold transition-colors select-none',
        sizeClasses[size],
        variantClasses[variant],
        className
      )}
    >
      {count}
    </div>
  );
};
